import React, { useState } from 'react';
import { ArrowRight, Ruler, Zap, FileText, Users, CheckCircle, Clock, LogIn, User, Home, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useAuth } from '../context/AuthContext';
import { LoginDialog } from '../components/LoginDialog';
import { APP_LOGO_SRC } from '../config/branding';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const navLinkClass =
    'text-[0.95rem] leading-[1.25rem] text-gray-600 hover:text-gray-900 transition-colors whitespace-nowrap shrink-0';

  const closeMobileNav = () => setMobileNavOpen(false);

  const goConfigurator = () => {
    closeMobileNav();
    navigate('/configurator-select', { state: { newQuote: true } });
  };

  const navLinks = (
    <>
      <a href="#features" className={navLinkClass} onClick={closeMobileNav}>{t('landing.navFeatures')}</a>
      <a href="#how-it-works" className={navLinkClass} onClick={closeMobileNav}>{t('landing.navHowItWorks')}</a>
      <a
        href="/pricing"
        className={navLinkClass}
        onClick={(e) => { e.preventDefault(); closeMobileNav(); navigate('/pricing'); }}
      >
        {t('landing.navPricing')}
      </a>
      <a
        href="/subsidiechecker"
        className={navLinkClass}
        onClick={(e) => { e.preventDefault(); closeMobileNav(); navigate('/subsidiechecker'); }}
      >
        {t('landing.navSubsidieChecker')}
      </a>
    </>
  );

  const authControls = (
    <>
      {isAuthenticated ? (
        <>
          <Button
            onClick={() => { closeMobileNav(); navigate('/dashboard'); }}
            variant="outline"
            size="sm"
            className="border-gray-300 whitespace-nowrap shrink-0"
          >
            <User className="mr-2 w-4 h-4 shrink-0" />
            Dashboard
          </Button>
          <Button
            onClick={() => { closeMobileNav(); logout(); }}
            variant="ghost"
            size="sm"
            className="text-gray-600 hover:text-gray-900 whitespace-nowrap shrink-0"
          >
            Logout
          </Button>
        </>
      ) : (
        <Button
          onClick={() => { closeMobileNav(); setLoginOpen(true); }}
          size="sm"
          className="bg-orange-600 hover:bg-orange-700 text-white whitespace-nowrap shrink-0"
        >
          <LogIn className="mr-2 w-4 h-4 shrink-0" />
          Login
        </Button>
      )}
    </>
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 gap-3">
            <div className="flex items-center shrink-0 min-w-0">
              <img
                src={APP_LOGO_SRC}
                alt="config-go"
                className="h-9 sm:h-10 w-auto object-contain"
              />
            </div>

            {/* Desktop — full nav from xl up (room for longer Dutch labels) */}
            <div className="hidden xl:flex items-center justify-end gap-4 2xl:gap-6 min-w-0 flex-1">
              <div className="flex items-center gap-5 2xl:gap-7">
                {navLinks}
              </div>
              <div className="flex items-center gap-2 2xl:gap-3 shrink-0">
                <Button
                  onClick={goConfigurator}
                  size="sm"
                  className="bg-orange-600 hover:bg-orange-700 whitespace-nowrap shrink-0"
                >
                  {t('landing.navTryConfigurator')}
                  <ArrowRight className="ml-2 w-4 h-4 shrink-0" />
                </Button>
                <LanguageSwitcher />
                {authControls}
              </div>
            </div>

            {/* Tablet / mobile — compact bar + menu */}
            <div className="flex xl:hidden items-center gap-2 shrink-0">
              <LanguageSwitcher compact />
              {isAuthenticated && (
                <Button
                  onClick={() => navigate('/dashboard')}
                  variant="outline"
                  size="icon"
                  className="border-gray-300 h-9 w-9 shrink-0"
                  aria-label="Dashboard"
                >
                  <User className="w-4 h-4" />
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => setMobileNavOpen((open) => !open)}
                aria-expanded={mobileNavOpen}
                aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {mobileNavOpen && (
          <div className="xl:hidden border-t border-gray-200 bg-white px-4 py-4 sm:px-6">
            <div className="flex flex-col gap-4 max-w-7xl mx-auto">
              {navLinks}
              <Button
                onClick={goConfigurator}
                className="w-full justify-center bg-orange-600 hover:bg-orange-700"
              >
                {t('landing.navTryConfigurator')}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-100">
                {authControls}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-orange-100 text-orange-800 border-orange-200">
                {t('landing.badgeForCompanies')}
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                {t('landing.heroTitle')}
                <span className="text-orange-600"> {t('landing.heroTitleHighlight')}</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {t('landing.heroDescription')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/configurator-select', { state: { newQuote: true } })}
                  className="bg-orange-600 hover:bg-orange-700 text-white text-lg px-8 py-6"
                >
                  {t('landing.ctaStartConfiguring')}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/subsidiechecker')}
                  className="border-2 border-orange-300 text-orange-700 hover:bg-orange-50 text-lg px-8 py-6"
                >
                  <Home className="mr-2 w-5 h-5" />
                  {t('landing.ctaSubsidieChecker')}
                </Button>
              </div>
              <div className="mt-8 flex items-center space-x-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  {t('landing.noCreditCard')}
                </div>
                <div className="flex items-center">
                  <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                  {t('landing.freeTrial')}
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-6 border-4 border-orange-500">
                <img 
                  src="/images/config.png" 
                  alt="Window Configuration"
                  className="rounded-lg w-full"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <Clock className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Average Time</div>
                      <div className="text-xl font-bold text-gray-900">5 min</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-orange-500">95%</div>
              <div className="text-slate-300 mt-2">{t('landing.statsAccuracy')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-500">3 Min</div>
              <div className="text-slate-300 mt-2">{t('landing.statsAvgConfig')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-500">500+</div>
              <div className="text-slate-300 mt-2">{t('landing.statsActiveCompanies')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-orange-500">50K+</div>
              <div className="text-slate-300 mt-2">{t('landing.statsQuotesGenerated')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-100 text-orange-800 border-orange-200">
              Powerful Features
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('landing.featuresTitle')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('landing.featuresDescription')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Ruler className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>{t('landing.featurePreciseMeasurements')}</CardTitle>
                <CardDescription>
                  {t('landing.featurePreciseMeasurementsDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>{t('landing.featureInstantQuotes')}</CardTitle>
                <CardDescription>
                  {t('landing.featureInstantQuotesDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>{t('landing.featureEngineerReady')}</CardTitle>
                <CardDescription>
                  {t('landing.featureEngineerReadyDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>{t('landing.featureMultiUser')}</CardTitle>
                <CardDescription>
                  {t('landing.featureMultiUserDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>{t('landing.featurePremiumSystems')}</CardTitle>
                <CardDescription>
                  {t('landing.featurePremiumSystemsDesc')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-2 hover:border-orange-500 transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>{t('landing.featureMobileOptimized')}</CardTitle>
                <CardDescription>
                  {t('landing.featureMobileOptimizedDesc')}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-orange-100 text-orange-800 border-orange-200">
              Simple Process
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('landing.howItWorksTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative">
              <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200 hover:border-orange-500 transition-all">
                <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('landing.step1Title')}</h3>
                <p className="text-gray-600">
                  {t('landing.step1Desc')}
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200 hover:border-orange-500 transition-all">
                <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('landing.step2Title')}</h3>
                <p className="text-gray-600">
                  {t('landing.step2Desc')}
                </p>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-xl p-8 shadow-lg border-2 border-gray-200 hover:border-orange-500 transition-all">
                <div className="w-16 h-16 bg-orange-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mb-6">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{t('landing.step3Title')}</h3>
                <p className="text-gray-600">
                  {t('landing.step3Desc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('landing.ctaSectionTitle')}
          </h2>
          <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
            {t('landing.ctaSectionDescription')}
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/configurator-select', { state: { newQuote: true } })}
            className="bg-white text-orange-600 hover:bg-slate-100 text-lg px-8 py-6"
          >
            {t('landing.ctaStartFreeTrial')}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Ruler className="w-8 h-8 text-orange-600" />
                <span className="text-xl font-bold text-white">Bestonline<span className="text-orange-600">kozijnen</span></span>
              </div>
              <p className="text-slate-400">
                {t('landing.footerDescription')}
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('landing.footerProduct')}</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="hover:text-orange-500 transition-colors">{t('landing.navFeatures')}</a></li>
                <li><a href="/pricing" onClick={(e) => { e.preventDefault(); navigate('/pricing'); }} className="hover:text-orange-500 transition-colors">{t('landing.navPricing')}</a></li>
                <li><a href="/configurator-select" onClick={(e) => { e.preventDefault(); navigate('/configurator-select', { state: { newQuote: true } }); }} className="hover:text-orange-500 transition-colors">{t('landing.navTryConfigurator')}</a></li>
                <li><a href="/subsidiechecker" onClick={(e) => { e.preventDefault(); navigate('/subsidiechecker'); }} className="hover:text-orange-500 transition-colors">{t('landing.navSubsidieChecker')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('landing.footerCompany')}</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-orange-500 transition-colors">{t('landing.footerAboutUs')}</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">{t('landing.footerContact')}</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">{t('landing.footerSupport')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">{t('landing.footerLegal')}</h4>
              <ul className="space-y-2">
                <li><a href="#" className="hover:text-orange-500 transition-colors">{t('landing.footerPrivacyPolicy')}</a></li>
                <li><a href="#" className="hover:text-orange-500 transition-colors">{t('landing.footerTermsOfService')}</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-400">
            <p>{t('landing.footerCopyright')}</p>
          </div>
        </div>
      </footer>
      
      <LoginDialog open={loginOpen} onOpenChange={setLoginOpen} />
    </div>
  );
};

export default LandingPage;

